import { FC, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack, Category, Save } from '@mui/icons-material'
import { Box, Button, Grid, Skeleton, Stack, Typography } from '@mui/material'

import GRImg from '../../../components/GRImg/GRImg'
import { LinearProgressBar } from '../../../components/LinearProgressBar/LinearProgressBar'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import {
  useGetActiveFeaturesQuery,
  useGetAnalysisCategoryFeaturesQuery,
  useGetAnalysisCategoryQuery,
  useGetRefreshedAnalysisQuery,
  useSaveAnalysisAnswersMutation,
} from '../../api/analysis'
import { AnalysisNote, useGetAnalysisNotesQuery } from '../../api/analysis-notes'
import { useGetInternalGameDocumentQuery } from '../../api/games'
import { useGetInternalGameScreenshotsQuery } from '../../api/screenshot-links'
import { AnalysisNoteEditDialog } from '../../components/AnalysisNoteEditDialog/AnalysisNoteEditDialog'
import { AnalysisNoteViewDialog } from '../../components/AnalysisNoteViewDialog/AnalysisNoteViewDialog'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { AnalysisGameBlock } from '../../features/analysis/components/AnalysisGameBlock/AnalysisGameBlock'
import { BenchmarkGames } from '../../features/analysis/components/BenchmarkGames/BenchmarkGames'
import { FeatureChoicesTable, ScreenshotsTarget } from '../../features/analysis/components/FeatureChoicesTable/FeatureChoicesTable'
import { FeatureImplementationsDialog } from '../../features/analysis/components/FeatureImplementationsDialog/FeatureImplementationsDialog'
import { getVersionsMarketIso } from '../../features/analysis/helpers/analysisOverviewHelpers'
import { getAnalysisOverviewPath } from '../../features/analysis/helpers/analysisPaths'
import {
  getCategoryAnswers,
  getChoicesProgress,
  sameAnswers,
  setAnswer,
  toAnswerItems,
  toChoiceFeatures,
  toggleAnswer,
} from '../../features/analysis/helpers/featureChoicesHelpers'
import { useAnalysisBenchmarks } from '../../features/analysis/hooks/useAnalysisBenchmarks'
import { useBenchmarkColumns } from '../../features/analysis/hooks/useBenchmarkColumns'
import { CategoryAnswers, InternalAnalysis } from '../../features/analysis/types/InternalAnalysis'
import { InternalGameDocument } from '../../features/games/types/InternalGame'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalCategoryFeature } from '../../types/InternalCategory'
import './AnalysisFeatureChoicesPage.scss'

type NoteTarget = { note: AnalysisNote; featureName: string; gameName?: string }
type ViewedNote = NoteTarget & { gameName: string }

export const AnalysisFeatureChoicesPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysis-feature-choices'))
  const { analysisId = '', categoryId = '' } = useParams<{ analysisId: string; categoryId: string }>()
  return <FeatureChoices key={`${analysisId}-${categoryId}`} analysisId={analysisId} categoryId={categoryId} />
}

const FeatureChoices: FC<{ analysisId: string; categoryId: string }> = ({ analysisId, categoryId }) => {
  const { t } = useTranslation()
  const analysisQuery = useGetRefreshedAnalysisQuery(analysisId, { skip: !analysisId, refetchOnMountOrArgChange: true })
  const analysis = analysisQuery.data
  const gameQuery = useGetInternalGameDocumentQuery(analysis?.gameId ?? '', { skip: !analysis })
  const featuresQuery = useGetAnalysisCategoryFeaturesQuery(
    { categoryId, marketIso: analysis?.marketIso ?? '' },
    { skip: !analysis || !categoryId, refetchOnMountOrArgChange: true }
  )

  if (analysisQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_overview_load_failed')}</Typography>
  if (gameQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_overview_game_load_failed')}</Typography>
  if (featuresQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_choices_features_failed')}</Typography>
  if (!analysis || !gameQuery.data || !featuresQuery.data) return <Skeleton variant="rectangular" height={480} />
  return <FeatureChoicesContent analysis={analysis} game={gameQuery.data} categoryId={categoryId} storedFeatures={featuresQuery.data} />
}

type FeatureChoicesContentProps = {
  analysis: InternalAnalysis
  game: InternalGameDocument
  categoryId: string
  storedFeatures: InternalCategoryFeature[]
}

const FeatureChoicesContent: FC<FeatureChoicesContentProps> = ({ analysis, game, categoryId, storedFeatures }) => {
  const { t } = useTranslation()
  const { run, notifyError, notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  const gameId = game.id ?? analysis.gameId
  const gameName = game.name ?? gameId
  const appId = game.appId || undefined
  const features = useMemo(() => toChoiceFeatures(storedFeatures), [storedFeatures])
  // Read once, so a refetch can't replace answers that weren't saved.
  const initialAnswers = useRef<CategoryAnswers>(getCategoryAnswers(analysis, categoryId, features))
  const [answers, setAnswers] = useState<CategoryAnswers>(initialAnswers.current)
  const [editedNote, setEditedNote] = useState<NoteTarget>()
  const [viewedNote, setViewedNote] = useState<ViewedNote>()
  const [screenshotsTarget, setScreenshotsTarget] = useState<ScreenshotsTarget>()

  const categoryQuery = useGetAnalysisCategoryQuery(categoryId)
  const notesQuery = useGetAnalysisNotesQuery(gameId)
  const screenshotsQuery = useGetInternalGameScreenshotsQuery(gameId)
  const activeFeaturesQuery = useGetActiveFeaturesQuery(
    {
      appId: appId ?? 0,
      version: analysis.gameVersion ?? '',
      marketIso: analysis.marketIso,
      store: analysis.storeId,
      versionMarketIso: getVersionsMarketIso(game, analysis.marketIso),
    },
    { skip: !appId }
  )
  const [saveAnswers, saveState] = useSaveAnalysisAnswersMutation()
  const benchmarkState = useAnalysisBenchmarks({ gameId, gameVersion: analysis.gameVersion })
  const benchmarkColumns = useBenchmarkColumns(benchmarkState.benchmarks)
  const position = useMemo(() => ({ storeId: analysis.storeId, marketIso: analysis.marketIso }), [analysis.marketIso, analysis.storeId])

  const isDirty = !sameAnswers(answers, initialAnswers.current)
  const { guard, leave } = useEditPageLeave(isDirty)
  const progress = getChoicesProgress(answers, features.length)
  const category = categoryQuery.data
  const overviewPath = getAnalysisOverviewPath(analysis.id, { benchmarks: benchmarkState.benchmarks, version: analysis.gameVersion })

  const handleSave = async () => {
    const saved = await run(saveAnswers({ id: analysis.id, categoryId, answers: toAnswerItems(answers) }).unwrap(), {
      error: t('internal-analysts:analysis_choices_save_failed'),
    })
    if (!saved.ok) return
    if (!sameAnswers(getCategoryAnswers(saved.result, categoryId, features), answers)) {
      notifyError(t('internal-analysts:analysis_choices_not_stored'))
      return
    }
    notifySuccess(t('internal-analysts:analysis_choices_saved', { name: category?.name ?? categoryId }))
    leave(overviewPath)
  }

  const openNoteEditor = (feature: InternalCategoryFeature, note?: AnalysisNote) =>
    setEditedNote({ note: note ?? { gameId, featureLegacyId: feature.legacyId, notes: '' }, featureName: feature.name })

  return (
    <Box className="AnalysisFeatureChoicesPage">
      <Stack direction="row" spacing={2} alignItems="flex-start" mb={3}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate(overviewPath)} sx={{ flexShrink: 0 }}>
          {t('internal-analysts:analysis_choices_back')}
        </Button>
        <Box
          className="AnalysisFeatureChoicesPage__category-icon"
          sx={{ width: 48, height: 48, flexShrink: 0, '& img': { width: '100%', height: '100%', objectFit: 'contain' } }}
        >
          {category?.icon?.url ? <GRImg alt="" src={category.icon.url} /> : <Category color="disabled" fontSize="large" />}
        </Box>
        <Box minWidth={0}>
          {categoryQuery.isLoading ? (
            <Skeleton width={240} height={36} />
          ) : (
            <Typography variant="h5" fontWeight="bold">
              {category?.name ?? t('internal-sidebar:analysis_feature_choices')}
            </Typography>
          )}
          {categoryQuery.isError && <Typography color="error.main">{t('internal-analysts:analysis_choices_category_failed')}</Typography>}
          {category?.desc && (
            <Typography variant="body2" color="text.secondary">
              {category.desc}
            </Typography>
          )}
        </Box>
      </Stack>

      <Grid container wrap="wrap" spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t('internal-analysts:analysis_choices_benchmark_help')}
            </Typography>
            <BenchmarkGames
              benchmarks={benchmarkState.benchmarks}
              disabled={benchmarkState.disabled}
              onPick={benchmarkState.pick}
              onRemove={benchmarkState.remove}
            />
            {benchmarkState.settingFailed && <Typography color="error.main">{t('internal-analysts:analysis_overview_benchmarks_load_failed')}</Typography>}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <AnalysisGameBlock title={t('internal-analysts:analysis_overview_currently_analyzing')} game={game} marketIso={analysis.marketIso} />
        </Grid>
      </Grid>

      {activeFeaturesQuery.isError && (
        <Typography color="error.main" mb={2}>
          {t('internal-analysts:analysis_choices_active_features_failed')}
        </Typography>
      )}
      {features.length ? (
        <FeatureChoicesTable
          features={features}
          answers={answers}
          gameName={gameName}
          position={position}
          activeFeatures={activeFeaturesQuery.data}
          benchmarkColumns={benchmarkColumns}
          screenshots={screenshotsQuery}
          notes={notesQuery}
          disabled={saveState.isLoading}
          onSelect={(featureId, weightId) => setAnswers((current) => setAnswer(current, featureId, weightId))}
          onToggle={(featureId, weightId) => setAnswers((current) => toggleAnswer(current, featureId, weightId))}
          onOpenScreenshots={setScreenshotsTarget}
          onEditNote={openNoteEditor}
          onViewBenchmarkNote={(column, feature, note) => setViewedNote({ note, featureName: feature.name, gameName: column.gameName })}
        />
      ) : (
        <Typography color="text.secondary">{t('internal-analysts:analysis_choices_no_features')}</Typography>
      )}

      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{ position: 'sticky', bottom: 0, mt: 3, py: 1.5, px: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', zIndex: 1 }}
      >
        <Box flexGrow={1} data-testid="choices-progress">
          <LinearProgressBar value={progress} labelAfter={`${progress}%`} color={progress === 100 ? 'success' : 'primary'} />
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saveState.isLoading || !features.length}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      <AnalysisNoteEditDialog
        open={!!editedNote}
        note={editedNote?.note}
        featureName={editedNote?.featureName}
        gameName={editedNote?.gameName}
        onClose={() => setEditedNote(undefined)}
      />
      <AnalysisNoteViewDialog
        open={!!viewedNote}
        title={t('internal-analysts:analysis_notes_view_title', { name: viewedNote?.featureName })}
        subtitle={viewedNote?.gameName}
        notes={viewedNote?.note.notes}
        onClose={() => setViewedNote(undefined)}
        onEdit={() => {
          if (viewedNote) setEditedNote({ note: viewedNote.note, featureName: viewedNote.featureName, gameName: viewedNote.gameName })
          setViewedNote(undefined)
        }}
      />
      <FeatureImplementationsDialog
        open={!!screenshotsTarget}
        gameName={screenshotsTarget?.gameName ?? ''}
        featureName={screenshotsTarget?.featureName ?? ''}
        screenshots={screenshotsTarget?.screenshots ?? []}
        onClose={() => setScreenshotsTarget(undefined)}
      />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:analysis_choices_discard_title')}
        text={t('internal-analysts:analysis_choices_discard_confirm')}
        discardText={t('internal-analysts:analysis_choices_discard')}
      />
    </Box>
  )
}
